import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Message from '@/design_system/Message';
import Paragraph from '@/design_system/Typography/Paragraph';
import { type ShipmentWithRelations, useUpdateDraftShipment } from '@/models/shipment';
import { ArticleSelect } from '@/routes/Shipments/Shipment/components/ShipmentForm/components/ArticleSelect';

import {
  getShipmentFormArticles,
  ShipmentFormArticle,
} from './ShipmentForm/components/ArticleSelect/ArticleSelect';
import {
  newShipmentFormPlace,
  ShipmentFormPlace,
} from './ShipmentForm/components/PlaceSelect/PlaceSelect';

export const AddArticlesModal = ({
  isOpen,
  setIsOpen,
  shipment,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  shipment: ShipmentWithRelations;
}) => {
  const { t } = useLingui();

  const [origin, setOrigin] = useState<ShipmentFormPlace | undefined>(
    shipment.originAddress
      ? newShipmentFormPlace(shipment.originAddress, shipment.originClient, shipment.originEntity)
      : undefined
  );
  const [destination, setDestination] = useState<ShipmentFormPlace | undefined>(
    shipment.destinationAddress
      ? newShipmentFormPlace(
          shipment.destinationAddress,
          shipment.destinationClient,
          shipment.destinationEntity
        )
      : undefined
  );

  const [addedArticles, setAddedArticles] = useState<ShipmentFormArticle[]>(
    shipment.articles.map(({ article }) => ({
      ...article,
      request: article.request
        ? {
            ...article.request,
            client: shipment.originClient,
          }
        : null,
      atEntity: shipment.originEntity
        ? {
            ...shipment.originEntity,
            address: shipment.originEntity.address,
            logisticsAddress: null,
          }
        : null,
      toEntity: shipment.destinationEntity
        ? {
            ...shipment.destinationEntity,
            address: shipment.destinationEntity.address,
            logisticsAddress: null,
          }
        : null,
      organization: shipment.organization,
      media: article.media ?? [],
    }))
  );
  const [error, setError] = useState<string>();

  const { mutateAsync: updateDraftShipment, isPending: isUpdateDraftShipmentPending } =
    useUpdateDraftShipment(shipment.id);

  const onSubmit = async () => {
    if (addedArticles.length === 0) {
      setError(
        t({
          id: 'shipment.add-articles-modal.error.add-one-item',
          message: 'Please add at least one item',
        })
      );

      return;
    }

    try {
      await updateDraftShipment({
        articles: addedArticles.map((addedArticle) => addedArticle.id),
      });

      setError('');
      setIsOpen(false);
    } catch (err) {
      console.error(err);
      setError(
        (err as Error).message ??
          t({
            id: 'shipment.add-articles-modal.error.unknown',
            message: 'Unknown error. Please contact Support',
          })
      );
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setAddedArticles(getShipmentFormArticles(shipment));
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={handleCancel}
      style={{ width: '62.5rem', maxHeight: '56.25rem' }}
      title={t({ id: 'shipment.add-articles-modal.title', message: 'Add items to the shipment' })}
    >
      <main>
        <ArticleSelect
          label={
            <Paragraph>
              <Trans id="shipments.add-articles-modal.search.label">
                You can add shippable items from other requests that match the destination.
              </Trans>
            </Paragraph>
          }
          ariaLabel={t({
            id: 'shipments.add-articles-modal.search.label',
            message: 'You can add shippable items from other requests that match the destination.',
          })}
          addedArticles={addedArticles}
          setAddedArticles={(articles) => {
            setAddedArticles(articles);
            setError('');
          }}
          origin={origin}
          setOrigin={setOrigin}
          destination={destination}
          setDestination={setDestination}
        />

        {error && (
          <Message style={{ marginTop: '0.5rem' }} type="error">
            {error}
          </Message>
        )}
      </main>

      <Footer>
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            onPress={handleCancel}
            disabled={isUpdateDraftShipmentPending}
            ariaLabel={t({ id: 'shipment.add-articles-modal.cancel', message: 'Cancel' })}
          >
            <Trans id="shipment.add-articles-modal.cancel">Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={onSubmit}
            isLoading={isUpdateDraftShipmentPending}
            ariaLabel={t({ id: 'shipment.add-articles-modal.save', message: 'Save' })}
          >
            <Trans id="shipment.add-articles-modal.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
